// utils/colorGenerator.js
// First install: npm install chroma-js

import chroma from 'chroma-js';

/**
 * Generates infinite unique colors using chroma.js
 * @param {number} count - Number of colors needed
 * @param {string} type - Type of color scheme
 * @returns {Array} Array of color hex codes
 */
export const generateInfiniteUniqueColors = (count, type = 'mixed') => {
  if (count <= 0) return [];
  
  const colorSchemes = {
    vibrant: {
      hueRange: [0, 360],
      saturationRange: [0.7, 0.9],
      lightnessRange: [0.4, 0.7]
    },
    pastel: {
      hueRange: [0, 360],
      saturationRange: [0.3, 0.5],
      lightnessRange: [0.7, 0.9]
    },
    blue_tones: {
      hueRange: [180, 240],
      saturationRange: [0.5, 0.8],
      lightnessRange: [0.3, 0.7]
    },
    mixed: {
      hueRange: [0, 360],
      saturationRange: [0.5, 0.8],
      lightnessRange: [0.4, 0.7]
    }
  };

  const scheme = colorSchemes[type] || colorSchemes.mixed;
  const colors = [];

  // Generate colors using different strategies for different ranges
  if (count <= 12) {
    // For small counts, use evenly distributed hues
    const hueStep = (scheme.hueRange[1] - scheme.hueRange[0]) / count;
    for (let i = 0; i < count; i++) {
      const hue = scheme.hueRange[0] + (hueStep * i);
      const saturation = scheme.saturationRange[0] + 
        Math.random() * (scheme.saturationRange[1] - scheme.saturationRange[0]);
      const lightness = scheme.lightnessRange[0] + 
        Math.random() * (scheme.lightnessRange[1] - scheme.lightnessRange[0]);
      
      colors.push(chroma.hsl(hue, saturation, lightness).hex());
    }
  } else if (count <= 50) {
    // For medium counts, use golden ratio for better distribution
    const goldenRatio = 0.618033988749;
    let hue = Math.random() * 360;
    
    for (let i = 0; i < count; i++) {
      hue += goldenRatio * 360;
      hue = hue % 360;
      
      const saturation = scheme.saturationRange[0] + 
        Math.random() * (scheme.saturationRange[1] - scheme.saturationRange[0]);
      const lightness = scheme.lightnessRange[0] + 
        Math.random() * (scheme.lightnessRange[1] - scheme.lightnessRange[0]);
      
      colors.push(chroma.hsl(hue, saturation, lightness).hex());
    }
  } else {
    // For large counts (100+), use systematic generation with variations
    const baseColors = chroma.scale(['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', 
                                   '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'])
                           .mode('lch')
                           .colors(Math.min(count, 20));
    
    // Generate variations of base colors
    for (let i = 0; i < count; i++) {
      if (i < baseColors.length) {
        colors.push(baseColors[i]);
      } else {
        // Create variations by adjusting lightness and saturation
        const baseIndex = i % baseColors.length;
        const baseColor = chroma(baseColors[baseIndex]);
        
        // Add variations
        const lightnessVariation = (Math.floor(i / baseColors.length) % 3 - 1) * 0.15;
        const saturationVariation = ((Math.floor(i / (baseColors.length * 3)) % 3) - 1) * 0.2;
        
        const modifiedColor = baseColor
          .set('hsl.l', Math.max(0.2, Math.min(0.8, baseColor.get('hsl.l') + lightnessVariation)))
          .set('hsl.s', Math.max(0.3, Math.min(0.9, baseColor.get('hsl.s') + saturationVariation)));
        
        colors.push(modifiedColor.hex());
      }
    }
  }

  // Ensure all colors are sufficiently different from each other
  return ensureColorDistinction(colors);
};

/**
 * Ensures colors are sufficiently different from each other
 * @param {Array} colors - Array of color hex codes
 * @returns {Array} Array of adjusted color hex codes
 */
const ensureColorDistinction = (colors) => {
  const minDistance = 30; // Minimum color distance
  const adjustedColors = [...colors];
  
  for (let i = 1; i < adjustedColors.length; i++) {
    for (let j = 0; j < i; j++) {
      const distance = chroma.deltaE(adjustedColors[i], adjustedColors[j]);
      
      if (distance < minDistance) {
        // Adjust the color to be more distinct
        const color = chroma(adjustedColors[i]);
        const hue = color.get('hsl.h');
        const newHue = (hue + 60 + (Math.random() * 60)) % 360;
        adjustedColors[i] = color.set('hsl.h', newHue).hex();
      }
    }
  }
  
  return adjustedColors;
};

/**
 * Gets colors for chart data with name-value pairs and handles short/long names
 * @param {Array} data - Array of objects with name, value, and optionally shortName properties
 * @param {string} colorType - Type of color scheme
 * @returns {Array} Array of objects with name, shortName, value, and color properties
 */
export const getColorsForChartData = (data, colorType = 'mixed') => {
  if (!data || !Array.isArray(data)) return [];
  console.log(data, "data100--")
  const colors = generateInfiniteUniqueColors(data.length, colorType);
  
  return data.map((item, index) => ({
    ...item,
    displayName: item.shortName || item.name, // For display in legend
    // fullName: item.name, // For tooltips and hover
    fullName: item.fullName, // For tooltips and hover
    color: colors[index]
  }));
};

/**
 * Utility function to truncate long names for display
 * @param {string} name - The name to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated name
 */
export const truncateName = (name, maxLength = 15) => {
  if (!name || name.length <= maxLength) return name;
  return name.substring(0, maxLength) + '...';
};

/**
 * Generate color palette using chroma.js scales
 * @param {number} count - Number of colors needed
 * @param {string} scaleType - Type of scale ('rainbow', 'viridis', 'plasma', etc.)
 * @returns {Array} Array of color hex codes
 */
export const generateColorScale = (count, scaleType = 'viridis') => {
  const scales = {
    rainbow: chroma.scale(['red', 'orange', 'yellow', 'green', 'blue', 'purple']),
    viridis: chroma.scale(['#440154', '#414487', '#2a788e', '#22a884', '#7ad151', '#fde725']),
    plasma: chroma.scale(['#0d0887', '#6a00a8', '#b12a90', '#e16462', '#fca636', '#f0f921']),
    cool: chroma.scale(['#00ffff', '#0080ff', '#8000ff']),
    warm: chroma.scale(['#ff0000', '#ff8000', '#ffff00']),
    earth: chroma.scale(['#8B4513', '#CD853F', '#DEB887', '#F4A460', '#D2691E'])
  };

  const scale = scales[scaleType] || scales.viridis;
  return scale.mode('lch').colors(count);
};